import {
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Radio,
  CircularProgress,
  RadioGroup,
  Select,
  TextField,
  Typography,
  FormHelperText,
} from '@material-ui/core';
import {isValidPolicyNumberPaperElectronic} from 'ApplicationEntry/models/EnteredPolicyNumber';
import * as I from 'ApplicationEntry/models/ProductsRiders/IWL/GroupIWL';
import States from 'ApplicationEntry/models/States';
import React, {useState} from 'react';
import {useDispatch} from 'react-redux';
import * as ErrorMessages from '../../ErrorMessages';
import * as BAActions from 'ApplicationEntry/ducks/billingAgent';
import * as IWLActions from 'ApplicationEntry/ducks/ProductsRiders/iwl';
import Alert from 'Shared/components/Alert';
import {Alert as WarningAlert} from '@material-ui/lab';
import FixedCodeRiderInformation from '../FixedCodeRiderInformation';
import {matchSalaryDeductionOccupationClassModel} from 'ApplicationEntry/models/ProductsRiders/SalaryDeductionOccupationClassModel';
import {CaseNumber, UnassignedCaseNumber} from 'ApplicationEntry/models/CaseNumber';
import {EmptyString} from 'Core/models/EmptyString';
import {CaseDetails, matchCaseDetailsByType} from 'ApplicationEntry/models/CaseDetails';
import PackageSelect from '../PackageSelect';
import {Plan} from 'ApplicationEntry/models/ProductsRiders/PlanModel';
import {ValidGroupCaseDetails} from 'ApplicationEntry/models/GroupCaseDetails';
import {getSitusStatefromCaseNumber} from 'ApplicationEntry/ducks/caseDetails';

type IWLProps = {
  iwl: I.GroupIWL;

  caseDetails: CaseDetails;
  plans: Plan[];
};

const GWL = ({iwl, plans, caseDetails}: IWLProps) => {
  const dispatch = useDispatch();
  const {
    plan,
    coverageLevel,
    type,
    faceAmt,
    policyNumber,
    priInsured,
    spouseInsured,
    replacement,
    riderInformation,
    calculatedPremium,
    premiumType,
    cwa,
    occClass,
    adb,
    sbVolume,
    premiumWaiver,
    product,
    jstamp,
    packageList,
    packageName,
    accidentalDeathBenefitTerminalIllness,
  } = iwl;

  const caseNumber: CaseNumber = matchCaseDetailsByType({
    Group: groupApplication => groupApplication.caseNumber,
    _: () => new UnassignedCaseNumber(new EmptyString()),
  })(caseDetails);

  const [planBlurred, setBlurPlan] = useState(false);
  const [policyNumberBlurred, setPolicyNumberBlurred] = useState(false);
  const [typeLabel, setTypeLabel] = useState<HTMLLabelElement | null>();
  const [coverageLevelLabel, setCoverageLevelLabel] = useState<HTMLLabelElement | null>();
  const [situsLabel, setSitusLabel] = useState<HTMLLabelElement | null>();
  const [insuredLabel, setInsuredLabel] = useState<HTMLLabelElement | null>();
  const showPlanError = planBlurred && plan.key === 'Empty';
  const showPolicyNumberError = policyNumberBlurred && !isValidPolicyNumberPaperElectronic(policyNumber) && replacement;
  let {situsState} = caseDetails as ValidGroupCaseDetails;

  return (
    <div className="iwl-section" id="iwl-section">
      <form noValidate autoComplete="off" className="iwl-section__form">
        <div className="iwl-section__form-row">
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <FormControl error={showPlanError} variant="outlined" className="iwl-section__form-text-field">
                <InputLabel ref={setTypeLabel} id="iwl-plan-label">
                  Plan
                </InputLabel>
                <Select
                  labelId="iwl-label-plan"
                  labelWidth={typeLabel?.clientWidth}
                  id="iwl-plan"
                  value={plan.key === 'Empty' ? '' : plan.value}
                  onBlur={() => setBlurPlan(true)}
                  onChange={e => {
                    const plan: Plan = plans.find(p => p.name! === e.target.value)!;

                    dispatch(
                      IWLActions.getOccClass(
                        caseNumber.key === 'Loaded'
                          ? caseNumber.Data
                          : caseNumber.key === 'UserAssignedCaseNumber'
                          ? caseNumber.value
                          : '',
                        plan
                      )
                    );
                    dispatch(IWLActions.changePlan(plan.name!));
                    dispatch(
                      IWLActions.getPackageList(
                        caseNumber.key === 'Loaded'
                          ? caseNumber.Data
                          : caseNumber.key === 'UserAssignedCaseNumber'
                          ? caseNumber.value
                          : '',
                        e.target.value as string
                      )
                    );
                  }}
                  displayEmpty
                >
                  {plans.map(plan => (
                    <MenuItem value={plan.name} key={plan.name}>
                      {plan.name}
                    </MenuItem>
                  ))}
                </Select>
                {showPlanError && <FormHelperText>{ErrorMessages.REQUIRED_MESSAGE}</FormHelperText>}
              </FormControl>
            </Grid>
          </Grid>

          {plan.key === 'NonEmpty' && (
            <>
              <Grid container spacing={4}>
                <Grid item xs={12} md={3}>
                  <FormControl variant="outlined" className="iwl-section__form-text-field">
                    <InputLabel ref={setTypeLabel} id="iwl-type-label">
                      Issue Type
                    </InputLabel>
                    <Select
                      labelId="iwl-label-plan"
                      labelWidth={typeLabel?.clientWidth}
                      id="iwl-typer"
                      value={type.key === 'Empty' ? '' : type.value}
                      onChange={e => dispatch(IWLActions.changeType(e.target.value as string))}
                      displayEmpty
                    >
                      <MenuItem value="SI">SI</MenuItem>
                      <MenuItem value="CGI">CGI</MenuItem>
                      <MenuItem value="GI">GI</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl variant="outlined" className="iwl-section__form-text-field">
                    <InputLabel ref={setCoverageLevelLabel} id="iwl-type-label">
                      Coverage Type
                    </InputLabel>
                    <Select
                      labelId="iwl-label-type"
                      labelWidth={coverageLevelLabel?.clientWidth}
                      id="iwl-type"
                      value={coverageLevel.key === 'Empty' ? '' : coverageLevel.value}
                      onChange={e => dispatch(IWLActions.changeCoverageLevel(e.target.value as string))}
                      displayEmpty
                    >
                      <MenuItem value="Employee">Employee</MenuItem>
                      <MenuItem value="Spouse">Spouse</MenuItem>
                      <MenuItem value="Child">Child</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl variant="outlined" className="iwl-section__form-text-field">
                    <InputLabel ref={setSitusLabel} id="iwl-situs-label">
                      Situs State
                    </InputLabel>
                    <Select
                      labelId="iwl-label-situs"
                      labelWidth={situsLabel?.clientWidth}
                      id="iwl-situs"
                      value={situsState.value || iwl.situsState.value}
                      onChange={e => {
                        dispatch(IWLActions.changeSitusState(e.target.value as string));
                        dispatch(getSitusStatefromCaseNumber(''));
                      }}
                      displayEmpty
                    >
                      {States.map(stateCode => (
                        <MenuItem key={stateCode} value={stateCode}>
                          {stateCode}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    id="iwl-faceAmt"
                    label="Face Amount"
                    variant="outlined"
                    placeholder="Face Amount"
                    type="number"
                    margin="normal"
                    fullWidth
                    className="iwl-section__form-text-field"
                    value={faceAmt.key === 'Empty' ? '' : faceAmt.value}
                    onChange={e => dispatch(IWLActions.changeFaceAmt(e.target.value as string))}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl variant="outlined" className="iwl-section__form-text-field">
                    <InputLabel ref={setInsuredLabel} id="iwl-primary-label">
                      Primary Insured
                    </InputLabel>
                    <Select
                      labelId="iwl-label-primary"
                      labelWidth={insuredLabel?.clientWidth}
                      id="iwl-primary"
                      value={priInsured.key === 'Empty' ? '' : priInsured.value}
                      onChange={e => dispatch(IWLActions.changePriInsured(e.target.value as string))}
                      displayEmpty
                    >
                      <MenuItem value="">&nbsp;&nbsp;</MenuItem>
                      <MenuItem value="Smoker">Smoker</MenuItem>
                      <MenuItem value="Non Smoker">Non Smoker</MenuItem>
                      <MenuItem value="Not Present">Not Present</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl variant="outlined" className="iwl-section__form-text-field">
                    <InputLabel ref={setTypeLabel} id="iwl-spouse-label">
                      Spouse
                    </InputLabel>
                    <Select
                      labelId="iwl-label-spouse"
                      labelWidth={typeLabel?.clientWidth}
                      id="iwl-spouse"
                      value={spouseInsured.key === 'Empty' ? '' : spouseInsured.value}
                      onChange={e => dispatch(IWLActions.changeSpouseInsured(e.target.value as string))}
                      displayEmpty
                      disabled={type.value === 'Spouse'}
                    >
                      <MenuItem value="">&nbsp;&nbsp;</MenuItem>
                      <MenuItem value="Smoker">Smoker</MenuItem>
                      <MenuItem value="Non Smoker">Non Smoker</MenuItem>
                      <MenuItem value="Not Present">Not Present</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        color="secondary"
                        checked={replacement}
                        onChange={() => dispatch(IWLActions.changeReplacement(!replacement))}
                        name="checkedB"
                      />
                    }
                    label={<Typography>Replacement</Typography>}
                    className="iwl-section__form-text-field"
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    id="iwl-polNum"
                    label="Policy Number"
                    variant="outlined"
                    placeholder="Enter Policy Number"
                    margin="normal"
                    fullWidth
                    error={showPolicyNumberError}
                    helperText={showPolicyNumberError ? ErrorMessages.INVALID_POLICY_NUMBER : ''}
                    className="iwl-section__form-text-field"
                    value={policyNumber.key !== 'ValidPolicyNumber' || !replacement ? '' : policyNumber.value.value}
                    onBlur={() => setPolicyNumberBlurred(true)}
                    onChange={e => dispatch(IWLActions.changePolicyNum(e.target.value as string))}
                    disabled={!replacement}
                    inputProps={{
                      maxLength: 10,
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <FormControl component="fieldset" className="iwl-section__form-radio-group">
                    <RadioGroup
                      aria-label="Premium Type"
                      name="premiumType"
                      value={premiumType.value}
                      onChange={e => dispatch(IWLActions.changePremiumType(e.target.value as string))}
                    >
                      <FormControlLabel
                        key="Modal"
                        value="Modal"
                        control={<Radio />}
                        label="Modal"
                        className="iwl-section__form-radio-button"
                      />
                      <FormControlLabel
                        key="Annual"
                        value="Annual"
                        control={<Radio />}
                        label="Annual"
                        className="iwl-section__form-radio-button"
                      />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    id="iwl-premium"
                    label="Premium"
                    variant="outlined"
                    placeholder="Premium"
                    margin="normal"
                    fullWidth
                    className="iwl-section__form-text-field"
                    value={calculatedPremium.key === 'Empty' ? '' : calculatedPremium.value}
                    onChange={event => {
                      dispatch(IWLActions.changePremium(event.target.value as string));
                      dispatch(IWLActions.changeCalculatedPremium(event.target.value as string));
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  {matchSalaryDeductionOccupationClassModel({
                    Loading: () => (
                      <TextField
                        label="Occ Class"
                        variant="filled"
                        placeholder="Occ Class"
                        className="iwl-section__text-field"
                        value={''}
                        InputProps={{
                          endAdornment: (
                            <CircularProgress className="case-details-section__loading-policy-number-icon" />
                          ),
                        }}
                      />
                    ),
                    Loaded: occClass => (
                      <TextField
                        id="iwl-occ-class"
                        label="OccClass"
                        variant="filled"
                        placeholder="OccClass"
                        inputProps={{readOnly: true}}
                        margin="normal"
                        fullWidth
                        className="iwl-section__form-text-field"
                        value={occClass.saldOccClassInfo.occClass}
                      />
                    ),
                    Unassigned: () => (
                      <TextField
                        id="iwl-occ-class"
                        label="OccClass"
                        variant="filled"
                        placeholder="OccClass"
                        inputProps={{readOnly: true}}
                        margin="normal"
                        fullWidth
                        className="iwl-section__form-text-field"
                        value={''}
                      />
                    ),
                    SalaryDeductionOccupationClassInfoWarning: message => (
                      <>
                        <TextField
                          id="iwl-occ-class"
                          label="OccClass"
                          variant="filled"
                          placeholder="OccClass"
                          inputProps={{readOnly: true}}
                          margin="normal"
                          fullWidth
                          className="iwl-section__form-text-field"
                          value={''}
                        />
                        <WarningAlert severity="warning" className="case-details-section__reserved-alert">
                          {message}
                        </WarningAlert>
                      </>
                    ),
                    Error: () => (
                      <Alert
                        className="billing-agent-section__policy-number-error-label"
                        message="An error occurred while attempting to retrieve an occupation class."
                      />
                    ),
                  })(occClass)}
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    id="iwl-cwa"
                    label="CWA"
                    variant="outlined"
                    placeholder="CWA"
                    margin="normal"
                    fullWidth
                    className="iwl-section__form-text-field"
                    value={cwa.key === 'Empty' ? '' : cwa.value}
                    onChange={e => dispatch(IWLActions.changeCWA(e.target.value as string))}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <PackageSelect
                    packageName={packageName}
                    packageList={packageList}
                    hidden={true}
                    changePackageAction={IWLActions.changePackageName}
                    handlePackageItems={(packageItem: object) => dispatch(IWLActions.changePackageItem(packageItem))}
                  />
                </Grid>
              </Grid>
              {plan.key === 'NonEmpty' ? (
                <FixedCodeRiderInformation
                  riderInformation={riderInformation}
                  changeRiderInformationAction={IWLActions.changeRiderInformation}
                  identifier={'group-iwl'}
                />
              ) : null}
            </>
          )}
        </div>
      </form>
    </div>
  );
};

export default GWL;
function matchCaseNumberByType(arg0: {Group: (groupApplication: any) => any; _: () => UnassignedCaseNumber}) {
  throw new Error('Function not implemented.');
}
